/* ─────────────────────────────────────────────────────────
 *  MIDI DAW – Playback Scheduler
 *
 *  Schedules MIDI note events through the synth engine
 *  using look-ahead scheduling for sample-accurate timing.
 *  Drives transport position updates via requestAnimationFrame.
 * ────────────────────────────────────────────────────────── */
import type { DawMidiFile, DawTrack, DawSynthPatch, TransportPosition } from '../types/midi-daw-types'
import type { DawSynthEngine } from './synth-engine'

const SCHEDULE_AHEAD = 0.1  // seconds to look ahead
const TICK_INTERVAL  = 25   // ms between scheduler ticks

export interface PlaybackCallbacks {
  onPositionUpdate: (pos: TransportPosition) => void
  onPlaybackEnd: () => void
}

/** Selection range in seconds for section playback */
export interface SelectionRange {
  startSec: number
  endSec: number
}

export class PlaybackScheduler {
  private ctx: AudioContext
  private synth: DawSynthEngine
  private midi: DawMidiFile | null = null
  private patches: Map<number, DawSynthPatch> = new Map()
  private cbs: PlaybackCallbacks

  private playing = false
  private startCtxTime = 0
  private startOffset = 0  // seek offset in song-seconds
  private scheduledUntil = 0
  private timerHandle = 0
  private rafHandle = 0

  // Real-time scale = BPM scale * user play-speed scale.
  // 1.0 means original tempo and 100% speed.
  private bpmScale = 1
  private speedScale = 1

  // Per-track scheduling cursors (note index already scheduled up to)
  private cursors: number[] = []

  // ── Metronome ──────────────────────
  private metronomeEnabled = false
  private metronomeVolume = 0.5
  private nextMetronomeBeat = 0  // next beat time in seconds to schedule

  // ── Loop & Selection ───────────────
  private looping = false
  private selection: SelectionRange | null = null

  constructor(
    ctx: AudioContext,
    synth: DawSynthEngine,
    callbacks: PlaybackCallbacks,
  ) {
    this.ctx = ctx
    this.synth = synth
    this.cbs = callbacks
  }

  /** Load a parsed MIDI file for playback */
  load(midi: DawMidiFile) {
    this.stop()
    this.midi = midi
    this.cursors = midi.tracks.map(() => 0)
  }

  /** Set synth patch for a track index */
  setPatch(trackIndex: number, patch: DawSynthPatch) {
    this.patches.set(trackIndex, patch)
  }

  /** Toggle metronome on/off */
  setMetronome(enabled: boolean) { this.metronomeEnabled = enabled }

  /** Set metronome volume 0–1 */
  setMetronomeVolume(v: number) { this.metronomeVolume = Math.max(0, Math.min(1, v)) }

  /** Enable/disable looping */
  setLooping(enabled: boolean) { this.looping = enabled }

  /** Set section selection (null clears it) */
  setSelection(sel: SelectionRange | null) { this.selection = sel }

  /** Set custom BPM scale relative to file BPM (1.0 = original tempo). */
  setBpmScale(scale: number) {
    const next = clampScale(scale)
    const current = this.getCurrentSeconds()
    this.bpmScale = next
    if (this.playing) this.reanchorAt(current)
  }

  /** Set user playback speed scale (1.0 = 100%). */
  setSpeedScale(scale: number) {
    const next = clampScale(scale)
    const current = this.getCurrentSeconds()
    this.speedScale = next
    if (this.playing) this.reanchorAt(current)
  }

  /** Start or resume playback from current offset */
  play(fromSeconds?: number) {
    if (!this.midi) return

    if (fromSeconds !== undefined) {
      this.startOffset = fromSeconds
      this.resetCursors(fromSeconds)
    }

    this.playing = true
    this.startCtxTime = this.ctx.currentTime
    this.scheduledUntil = 0

    // Reset metronome beat cursor
    this.resetMetronomeCursor(this.startOffset)

    // Start look-ahead scheduler
    this.schedulerTick()
    this.timerHandle = window.setInterval(() => this.schedulerTick(), TICK_INTERVAL)
    this.positionLoop()
  }

  /** Current transport time in seconds (read-only for UI policy decisions). */
  getCurrentSeconds(): number {
    return this.playing ? this.currentSeconds() : this.startOffset
  }

  /** Pause playback */
  pause() {
    if (!this.playing) return
    this.playing = false
    this.startOffset = this.currentSeconds()
    clearInterval(this.timerHandle)
    cancelAnimationFrame(this.rafHandle)
    this.synth.panic()
  }

  /** Stop and reset to beginning */
  stop() {
    this.playing = false
    this.startOffset = 0
    clearInterval(this.timerHandle)
    cancelAnimationFrame(this.rafHandle)
    this.synth.panic()
    if (this.midi) {
      this.cursors = this.midi.tracks.map(() => 0)
    }
    this.cbs.onPositionUpdate(this.buildPosition(0))
  }

  /** Seek to a position in seconds */
  seek(seconds: number) {
    const wasPlaying = this.playing
    if (wasPlaying) this.pause()
    this.startOffset = seconds
    this.resetCursors(seconds)
    this.cbs.onPositionUpdate(this.buildPosition(seconds))
    if (wasPlaying) this.play()
  }

  isPlaying() { return this.playing }

  dispose() {
    this.stop()
  }

  // ── Internal scheduling ──────────────────────────────────

  private schedulerTick() {
    if (!this.midi || !this.playing) return

    const elapsed = this.currentSeconds()

    // ── End-of-region check (loop or stop) ──
    const regionEnd = this.selection ? this.selection.endSec : this.midi.durationSeconds
    const scheduleEnd = Math.min(elapsed + SCHEDULE_AHEAD, regionEnd)
    if (elapsed >= regionEnd) {
      if (this.looping) {
        const loopStart = this.selection ? this.selection.startSec : 0
        this.synth.panic()
        this.startOffset = loopStart
        this.startCtxTime = this.ctx.currentTime
        this.resetCursors(loopStart)
        this.resetMetronomeCursor(loopStart)
        this.scheduledUntil = 0
        this.schedulerTick()  // schedule loop-start notes immediately, no 25ms gap
        return
      }
      this.stop()
      this.cbs.onPlaybackEnd()
      return
    }

    // ── Metronome clicks ───────────────
    if (this.metronomeEnabled) {
      this.scheduleMetronomeClicks(elapsed, scheduleEnd)
    }

    // Hoist solo check outside the per-track loop
    const hasSolo = this.midi.tracks.some(t => t.solo)

    for (let ti = 0; ti < this.midi.tracks.length; ti++) {
      const track = this.midi.tracks[ti]
      if (track.muted) continue
      if (hasSolo && !track.solo) continue

      const patch = this.patches.get(ti)
      if (!patch) continue

      while (this.cursors[ti] < track.notes.length) {
        const note = track.notes[this.cursors[ti]]
        if (note.time > scheduleEnd) break

        if (note.time >= elapsed - 0.01) {
          const audioTime = this.startCtxTime + (note.time - this.startOffset) / this.timeScale()
          const key = `${ti}:${note.midi}:${this.cursors[ti]}`

          this.synth.scheduleNote(
            note.midi,
            note.velocity,
            Math.max(audioTime, this.ctx.currentTime),
            note.duration,
            patch,
            track.volume,
            key,
          )
        }

        this.cursors[ti]++
      }
    }

    this.scheduledUntil = scheduleEnd
  }

  private positionLoop() {
    if (!this.playing) return
    const seconds = this.currentSeconds()
    this.cbs.onPositionUpdate(this.buildPosition(seconds))
    this.rafHandle = requestAnimationFrame(() => this.positionLoop())
  }

  private currentSeconds(): number {
    return this.startOffset + (this.ctx.currentTime - this.startCtxTime) * this.timeScale()
  }

  private timeScale(): number {
    return this.bpmScale * this.speedScale
  }

  private reanchorAt(songSeconds: number) {
    this.startOffset = songSeconds
    this.startCtxTime = this.ctx.currentTime
    this.resetCursors(songSeconds)
    this.resetMetronomeCursor(songSeconds)
    this.scheduledUntil = songSeconds
  }

  private buildPosition(seconds: number): TransportPosition {
    if (!this.midi) {
      return { seconds: 0, ticks: 0, bar: 1, beat: 1, pulse: 0 }
    }

    const { bpm, ppq, timeSignatureNumerator } = this.midi
    const beatsPerSecond = bpm / 60
    const totalBeats = seconds * beatsPerSecond
    const ticksPerBeat = ppq
    const ticks = Math.floor(totalBeats * ticksPerBeat)

    const bar = Math.floor(totalBeats / timeSignatureNumerator) + 1
    const beat = Math.floor(totalBeats % timeSignatureNumerator) + 1
    const pulse = Math.floor((totalBeats % 1) * ppq)

    return { seconds, ticks, bar, beat, pulse }
  }

  private resetCursors(fromSeconds: number) {
    if (!this.midi) return
    this.cursors = this.midi.tracks.map(track => {
      // Binary search for first note >= fromSeconds
      let lo = 0, hi = track.notes.length
      while (lo < hi) {
        const mid = (lo + hi) >>> 1
        if (track.notes[mid].time < fromSeconds) lo = mid + 1
        else hi = mid
      }
      return lo
    })
  }

  // ── Metronome helpers ────────────────────────────────────

  private resetMetronomeCursor(fromSeconds: number) {
    if (!this.midi) return
    const beatDuration = 60 / this.midi.bpm
    // Snap to the beat at or just before fromSeconds
    this.nextMetronomeBeat = Math.ceil(fromSeconds / beatDuration) * beatDuration
  }

  private scheduleMetronomeClicks(elapsed: number, scheduleEnd: number) {
    if (!this.midi) return
    const beatDuration = 60 / this.midi.bpm
    const { timeSignatureNumerator } = this.midi

    while (this.nextMetronomeBeat <= scheduleEnd) {
      if (this.nextMetronomeBeat >= elapsed - 0.01) {
        const audioTime = Math.max(
          this.startCtxTime + (this.nextMetronomeBeat - this.startOffset) / this.timeScale(),
          this.ctx.currentTime,
        )
        // Determine if this is beat 1 (downbeat) for accent
        const beatIndex = Math.round(this.nextMetronomeBeat / beatDuration)
        const isDownbeat = beatIndex % timeSignatureNumerator === 0
        this.playClick(audioTime, isDownbeat)
      }
      this.nextMetronomeBeat += beatDuration
    }
  }

  /** Schedule a single metronome click at audioTime */
  private playClick(audioTime: number, accent: boolean) {
    const osc = this.ctx.createOscillator()
    const gain = this.ctx.createGain()
    osc.type = 'sine'
    // Downbeat = higher pitch accent
    osc.frequency.setValueAtTime(accent ? 1500 : 1000, audioTime)
    gain.gain.setValueAtTime(this.metronomeVolume * (accent ? 0.7 : 0.45), audioTime)
    gain.gain.exponentialRampToValueAtTime(0.001, audioTime + 0.05)
    osc.connect(gain)
    gain.connect(this.ctx.destination)  // bypass master gain — always audible
    osc.start(audioTime)
    osc.stop(audioTime + 0.06)
    osc.onended = () => { gain.disconnect(); osc.disconnect() }
  }

  /** Get the closest beat boundary in seconds for snap-to-beat seek */
  snapToBeat(seconds: number): number {
    if (!this.midi) return seconds
    const beatDuration = 60 / this.midi.bpm
    return Math.round(seconds / beatDuration) * beatDuration
  }
}

function clampScale(v: number): number {
  if (!Number.isFinite(v)) return 1
  return Math.max(0.25, Math.min(4, v))
}
